'use client';

import { useCallback, useState } from 'react';
import { useTranslations } from 'next-intl';

import { cn } from '@/lib/styleUtils';
import { DraggableCar } from '@/modules/captcha/components/CarSprite';
import { PuzzlePrompt, PuzzleScene } from '@/modules/captcha/components/PuzzleFrame';
import { usePuzzleDrag } from '@/modules/captcha/hooks/usePuzzleDrag';
import { BAY_WIDTH, GATE_LEFTS, randomInt } from '@/modules/captcha/shared/puzzle-utils';
import { draggableCar } from './puzzle.style';
import type { PuzzleProps } from './puzzle.types';

const SCENE_HEIGHT = 232;
const HOME = { x: 133, y: 150 };
const BOUNDS = { x: 266, y: 160 };
const GATE_ROW_BOTTOM = 115;

/**
 * Design 2c — drag the car through the one gate that is open.
 *
 * ## Invariants:
 * - The open gate is distinguished by TWO signals: a lifted barrier and a green lamp, against a
 *   striped barrier and a red lamp. Why: colour alone fails for the ~8% of men with red/green
 *   colour blindness, and this modal sits in front of a payment — an unpassable challenge is a
 *   lost booking, not a minor accessibility gap.
 */
export const DriveThroughGate = ({ onSolved }: PuzzleProps) => {
  const t = useTranslations('Captcha');
  const [openGate] = useState(() => randomInt(GATE_LEFTS.length));

  const onDrop = useCallback(
    ({ x, y }: { x: number; y: number }) => {
      const centerX = x + 23;
      const centerY = y + 36;
      if (centerY >= GATE_ROW_BOTTOM) return { accepted: false };

      const hit = GATE_LEFTS.findIndex((left) => centerX >= left && centerX <= left + BAY_WIDTH);
      if (hit !== openGate) return { accepted: false };

      onSolved();
      return { accepted: true, snapTo: { x: GATE_LEFTS[hit] + 10, y: 22 } };
    },
    [onSolved, openGate]
  );

  const { position, dragging, shaking, handlers } = usePuzzleDrag({
    home: HOME,
    bounds: BOUNDS,
    onDrop,
  });

  return (
    <>
      <PuzzlePrompt>{t('driveThroughGatePrompt')}</PuzzlePrompt>
      <PuzzleScene height={SCENE_HEIGHT} data-cy="captcha-drive-through-gate">
        {GATE_LEFTS.map((left, index) => {
          const isOpen = index === openGate;
          return (
            <div
              key={left}
              className={cn(
                'tw:absolute tw:top-small tw:box-border tw:h-[92px] tw:w-[66px] tw:overflow-hidden',
                'tw:rounded-t-4 tw:border-2 tw:bg-white',
                isOpen ? 'tw:border-success' : 'tw:border-gray-light'
              )}
              style={{ left }}
              data-cy={`captcha-gate-${index}`}
            >
              {isOpen ? (
                <>
                  <div className="tw:absolute tw:inset-nano tw:rounded-2 tw:bg-gray-darker" />
                  <div className="tw:absolute tw:left-nano tw:right-nano tw:top-nano tw:h-[10px] tw:rounded-t-2 tw:bg-gray-light" />
                  <div className="tw:absolute tw:right-micro tw:top-[18px] tw:h-micro tw:w-micro tw:rounded-full tw:bg-success tw:shadow-[0_0_6px_var(--tw-color-success)]" />
                </>
              ) : (
                <>
                  <div className="tw:absolute tw:inset-nano tw:rounded-2 tw:bg-[repeating-linear-gradient(0deg,var(--tw-color-gray-lighter)_0_10px,var(--tw-color-gray-light)_10px_13px)]" />
                  <div className="tw:absolute tw:right-micro tw:top-micro tw:h-micro tw:w-micro tw:rounded-full tw:bg-error" />
                </>
              )}
            </div>
          );
        })}
        <div
          {...handlers}
          role="button"
          tabIndex={0}
          aria-label={t('carHandleLabel')}
          className={draggableCar}
          style={{
            transform: `translate(${position.x}px, ${position.y}px)`,
            transition: dragging ? 'none' : 'transform 0.25s ease',
          }}
          data-cy="captcha-drive-through-gate-handle"
        >
          <DraggableCar shake={shaking} />
        </div>
      </PuzzleScene>
    </>
  );
};

DriveThroughGate.displayName = 'DriveThroughGate';
